import { Icon, Table, Typography } from '@statsbomb/kitbag-components';
import { VideoStatus } from '@statsbomb/parachute-types';
import { useAtomValue } from 'jotai';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { sectionAtom } from '@/atoms/section';
import {
  calculate360MetricKeys,
  calculatePhysicalMetricKeys,
  resolveCellTooltip,
} from '@/components/tables/AggsTable.utils';
import { CellValueToDisplay } from '@/components/tables/CellValueToDisplay';
import { GAME_ID, METRIC_KEY } from '@/consts/searchKeys';
import { useSortTable } from '@/hooks/useSortTable';
import { MetricDefinition } from '@/types/metric';
import { NestedObject } from '@/types/object';
import { StatusTranslationKey } from '@/types/status';
import { DataTableColumn } from '@/types/table';
import { getMetricKeyFromColumnKey } from '@/utils/metrics';
import { getDataType, getFormatRule, getHighlightedColumnIndex, isLowerBetter, isMetricColumn } from '@/utils/table';
import { getTranslationColumns } from '@/utils/translations';
import { ContentState } from '../contentState/ContentState';
import { MetricTooltip } from '../metricTooltip/MetricTooltip';
import { PlayGameAggsVideoButton } from '../video/PlayVideoButton';
import { PERMANENT_COLUMNS, GAMES_STICKY_COLUMNS } from './GameAggregatesTable.constants';
import { PhysicalMetricHeader } from './PhysicalMetricHeader';
import { TeamGameWatchCell } from './TeamGameWatchCell';
import { TeamGameWatchHeaderCell } from './TeamGameWatchHeaderCell';

const ProcessingAggregates = () => {
  const { t } = useTranslation('games');

  return (
    <div className="flex flex-row gap-x-1">
      <Icon variant="Info" size="small" />
      <Typography variant="bodyMedium" as="span">
        {t('processingAggregates')}
      </Typography>
    </div>
  );
};

interface GameAggregatesTableProps {
  data?: NestedObject[];
  isLoading?: boolean;
  visibleColumns?: string[];
  availableColumns?: DataTableColumn[];
  metricData?: MetricDefinition[];
}

const getLinkParams = (key: string, gameId: string) => {
  const metricKey = getMetricKeyFromColumnKey(key);
  // We don't expect metricKey to be undefined here, but dealing with that possibility.
  if (!metricKey)
    // eslint-disable-next-line no-console
    console.error('Attempting to render game aggs table with a non-metric column key:', {
      key,
      gameId,
    });
  return metricKey ? `?${METRIC_KEY}=${metricKey}&${GAME_ID}=${gameId}` : '';
};

export const GameAggregatesTable = ({
  data = [],
  isLoading = false,
  visibleColumns = [],
  availableColumns,
  metricData = [],
}: GameAggregatesTableProps) => {
  const { t } = useTranslation(['events', 'games', 'video']);

  const [openMenuRow, setOpenMenuRow] = useState<number | null>(null);
  const { orderBy, handleSort, getSortedState } = useSortTable(true);
  const section = useAtomValue(sectionAtom);
  const isTeamSection = section === 'team';

  const hasData = data.length > 0;
  const isBelowXl = useTheme().mqMatch('xl');
  const stickyColumns = !isBelowXl ? GAMES_STICKY_COLUMNS : undefined;

  const sortedColumnIndex = orderBy ? visibleColumns.findIndex(col => col === orderBy) : -1;
  // TODO (PPC-812: remove isTeamSection condition once implemented player game stats video menu)
  const highlightedColumnIndex =
    isTeamSection && sortedColumnIndex === 0 ? 1 : getHighlightedColumnIndex(sortedColumnIndex, isTeamSection);
  const threeSixtyMetricKeys = calculate360MetricKeys(metricData);
  const physicalMetricKeys = calculatePhysicalMetricKeys(metricData);

  return (
    <>
      <div className={hasData ? 'overflow-x-auto' : 'overflow-x-hidden'}>
        <Table isHeadSticky highlightedColumn={highlightedColumnIndex} stickyColumns={stickyColumns}>
          <Table.Head>
            <Table.Row>
              {visibleColumns.map((key, index) => {
                const formatRule = getFormatRule(key, availableColumns);
                const { translationKey, translationNs } = getTranslationColumns(key);
                const isPhysical = isMetricColumn(key) && physicalMetricKeys.has(getMetricKeyFromColumnKey(key));
                return (
                  <Fragment key={key}>
                    <Table.HeaderCell
                      dataType={getDataType(formatRule)}
                      sortCb={() => handleSort(key, isLowerBetter(formatRule))}
                      sortState={getSortedState(key)}
                      size="regular"
                      wrapText={isMetricColumn(key)}
                    >
                      {isPhysical ? (
                        <PhysicalMetricHeader
                          metricKey={key}
                          translationNs={translationNs}
                          label={t(translationKey, { ns: translationNs })}
                          testId={`column-${index}`}
                        />
                      ) : (
                        <MetricTooltip metricKey={key} translationNs={translationNs}>
                          <span data-testid={`column-${index}`}>{t(translationKey, { ns: translationNs })}</span>
                        </MetricTooltip>
                      )}
                    </Table.HeaderCell>
                    {index === 0 && isTeamSection && <TeamGameWatchHeaderCell />}
                  </Fragment>
                );
              })}
            </Table.Row>
          </Table.Head>

          {hasData && (
            <Table.Body>
              {data.map((row, rowIndex) => {
                const gameId = String(row['game.gameId']);
                const videoStatus = row['userGame.videoStatus'] as VideoStatus;
                const columnsToRenderValues = row.hasAggregates ? visibleColumns : PERMANENT_COLUMNS;

                return (
                  <Table.Row key={gameId} className="[&:hover_.invisible]:visible [&:focus-within_.invisible]:visible">
                    {columnsToRenderValues.map((tableColumnKey, columnIndex) => {
                      const formatRule = getFormatRule(tableColumnKey, availableColumns);
                      const value = row[tableColumnKey];
                      const metricKey = getMetricKeyFromColumnKey(tableColumnKey);
                      const linkParams = getLinkParams(tableColumnKey, gameId);
                      const key = `${gameId}-${tableColumnKey}`;
                      const isPhysical = physicalMetricKeys.has(metricKey);
                      const { tooltipContent, showAsteriskWithTooltip } = resolveCellTooltip(
                        t,
                        metricKey,
                        threeSixtyMetricKeys,
                        row,
                        'no360DataForGame',
                        { isPhysical, value },
                      );
                      const showVideoButton = isMetricColumn(tableColumnKey) && !!value && !isPhysical;
                      return (
                        <Fragment key={key}>
                          <CellValueToDisplay
                            columnKey={tableColumnKey}
                            formatRule={formatRule}
                            showAsteriskWithTooltip={showAsteriskWithTooltip}
                            showNullAsNa
                            t={t}
                            testId={key}
                            tooltipContent={tooltipContent}
                            value={value}
                          >
                            {showVideoButton && (
                              <PlayGameAggsVideoButton
                                gameId={gameId}
                                videoStatus={videoStatus}
                                linkParams={linkParams}
                                metricKey={getMetricKeyFromColumnKey(key)}
                              />
                            )}
                          </CellValueToDisplay>
                          {/* TODO (PPC-812: Implement player game stats video menu) */}
                          {columnIndex === 0 && isTeamSection && (
                            <TeamGameWatchCell
                              gameId={Number(gameId)}
                              gameTotalGoals={
                                Number(row['aggregates.goalsAndOwnGoals']) +
                                Number(row['aggregates.goalsAndOwnGoalsConceded'])
                              }
                              videoStatus={videoStatus}
                              isMenuOpen={openMenuRow === rowIndex}
                              onMenuOpen={() => setOpenMenuRow(rowIndex)}
                              onMenuClose={() => setOpenMenuRow(null)}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                    {!row.hasAggregates &&
                      visibleColumns.map((_, index) =>
                        index !== 0 ? null : (
                          <Table.DataCell key={`${gameId}-processing-aggs`} colSpan={visibleColumns.length}>
                            <ProcessingAggregates />
                          </Table.DataCell>
                        ),
                      )}
                  </Table.Row>
                );
              })}
            </Table.Body>
          )}
        </Table>
      </div>
      {!isLoading && !hasData && <ContentState status={StatusTranslationKey.NO_DATA} />}
      {isLoading && <ContentState status={StatusTranslationKey.LOADING} />}
    </>
  );
};
